import { get } from 'Api';
import store from 'Redux/store';

/**
 * Given a list of products (a), remove any product that exists in list b
 * @param  {Array.Object} listA List of products to filter
 * @param  {Array.Object} listB List of products to check for duplicates
 * @param  {String} keyA        Key to test on listA
 * @param  {String} keyB        Key to test on listB
 * @return {Array.Object}       Filtered list
 */
const removeDuplicates = (listA, listB, keyA, keyB) =>
  listA.filter(a => listB.every(b => a[keyA] !== b[keyB]));

/**
 * Given a list of products, remove any product that matches a given slug
 * @param  {Array.Object} products List of products to filter
 * @param  {String} currentSlug    Slug to check for
 * @return {Array.Object}          Filtered list
 */
const removeMatchingSlug = (products, currentSlug) =>
  products.filter(product => product.slug !== currentSlug);

/**
 * Given a list of products, remove any product that has giveaway ticked
 * @param  {Array.Object} products List of products to filter
 * @return {Array.Object}          Filtered list
 */
const removeGiveaways = products =>
  products.filter(product => !product.giveaway);

/**
 * Run filters on recommendations to remove any matches for
 * the basket and current page
 * @param  {Array.Object} recommendations List of products to filter
 * @param  {Array.Object} basketItems     Items in the basket
 * @param  {String} currentSlug           Slug of current page to check for
 * @return {Array.Object}                 Filtered results
 */
const filterRecommendations = (recommendations, basketItems, currentSlug) => {
  let filtered = removeDuplicates(recommendations, basketItems, 'product_id', '_id');
  filtered = removeMatchingSlug(filtered, currentSlug);
  filtered = removeGiveaways(filtered);
  return filtered;
};

/**
 * Cut the array of recommendations down to a given length
 * @param  {Array.Object} recommendations List of products to filter
 * @param  {Number} length                [description]
 * @return {Array.Object}                 Filtered results
 */
const limitRecommendations = (products, length) => products.slice(0, length);

/**
 * Clean existing recommendations
 * @return {Array.Object}             Product hits
 * @param  {String} currentSlug       Slug of current product
 * @param  {Array.Object} basketItems Items in the basket
 * @param  {Number} [count=4]         How many results to return
 * @return {Array.Object}             Product hits
 */
export function cleanRecommendations(hits, currentSlug, basketItems, count = 14) {
  const recommendations = filterRecommendations(hits, basketItems, currentSlug);
  return limitRecommendations(recommendations, count);
}

/**
 * Fetch recommendations from server (smart or dumb)
 * @param  {Number} count             Number of results
 * @param  {Array.Object} basketItems Basket items (to remove duplicates)
 * @param  {Boolean} [smart=false]    Use smart recommendations?
 * @return {Array.Object}             Product hits
 */
export function fetchCartRecommendations(count, basketItems, smart = false) {
  return get(store.dispatch, smart ? '/algolia/smart-cart-recommendations' : '/algolia/cart-recommendations')
    .then(({ response, data }) => {
      if (response && Array.isArray(data) && data.length) {
        return cleanRecommendations(data, null, basketItems, count);
      }
      return [];
    });
}

/**
 * Fetch recommendations for "You may also like sections"
 * @param  {String} type              Type of products (game, bundle, dlc)
 * @param  {String} currentSlug       Slug of current product
 * @param  {Array.Object} basketItems Items in the basket
 * @param  {Array.String} franchises  Array of franchises to search for
 * @param  {Number} [count=4]         How many results to return
 * @return {Array.Object}             Product hits
 */
export function fetchRecommendations(
  type,
  currentSlug,
  basketItems,
  franchises,
  count = 14,
  cachedHits,
) {
  const isAltRank = false;
  let url = `/algolia/recommendations/${type === 'dlc' ? 'game' : type}`;

  if (franchises && franchises.length && !cachedHits) {
    url += `?franchises=${encodeURI(franchises.join(','))}`;
  } else if (type === 'cart' && !cachedHits) {
    url = '/algolia/cart-recommendations';
  } else if (isAltRank) {
    url += '?altRank=1';
  }

  return dispatch => get(dispatch, url)
    .then(({ response, data }) => {
      if (response && Array.isArray(data) && data.length) {
        const recommendations = filterRecommendations(data, basketItems, currentSlug);

        return limitRecommendations(recommendations, count);
      }
      return [];
    });
}
